import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { InsertDriveFile as InsertDriveFileIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import { FileDto } from '../Document.interface';
import DocumentFileExplorer from './DocumentFileExplorer';

const StyledList = styled(List)`
  padding: 0;
  background-color: ${props => props.theme.palette.grey[100]};
  .MuiAvatar-root {
    background-color: ${props => props.theme.palette.primary.main};
  }
`;

const docRegExp: RegExp = /\.doc(?:x)?$/;

export interface DocumentTemplateDialogProps {
  file?: FileDto;
  error?: FetchError;
  isLoading?: boolean;
  title?: string;
  open: boolean;
  unsetLabel?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  hideUnsetButton?: boolean;
  forceConfirmButton?: boolean;
  disableConfirmButton?: boolean;
  content?: React.ReactNode;
  setOpen: (open: boolean) => void;
  onSelectFile?: (file: FileDto | undefined) => void;
}

const DocumentTemplateDialog: React.FC<DocumentTemplateDialogProps> = (
  props: DocumentTemplateDialogProps
) => {
  const {
    file: initialFile,
    error,
    content,
    open = false,
    isLoading = false,
    hideUnsetButton = false,
    forceConfirmButton = false,
    disableConfirmButton = false,
    title = 'Set Document Template',
    unsetLabel = 'Use default template',
    cancelLabel = 'Cancel',
    confirmLabel = 'Set as default template',
    setOpen,
    onSelectFile
  } = props;
  const initialFileRef = useRef<FileDto | undefined>();
  const [file, setFile] = useState<FileDto | undefined>(initialFile);
  const storageKey: string | undefined = file?.storageKey;
  const extension: string = storageKey
    ? storageKey.slice(storageKey.lastIndexOf('.'))
    : '?';
  const canUndo: boolean = initialFile?.id !== file?.id;

  useEffect(() => {
    // only update file if initialFile changed id
    if (
      !initialFileRef.current ||
      initialFileRef.current.id !== initialFile?.id
    ) {
      initialFileRef.current = initialFile;
      setFile(initialFile);
    }
  }, [initialFile]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {content}
        <Box mb={2}>
          <DialogContentText>
            Select a new document (<strong>DOCX</strong>) template or use
            default.
          </DialogContentText>
        </Box>

        <DocumentFileExplorer
          hideTrashDir
          includeFiles
          selected={file ? file.id.toString() : undefined}
          onSelectFile={(_, file: FileDto) => {
            setFile(!file?.isFolder ? file : undefined);
          }}
        />
        <Collapse in={!!file}>
          <Box mt={2} boxShadow={1}>
            <StyledList dense>
              <ListItem>
                <ListItemAvatar>
                  <Avatar>
                    <InsertDriveFileIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={file?.name || '?'}
                  secondary='This file will be used as the template document.'
                />
              </ListItem>
            </StyledList>
          </Box>
        </Collapse>

        <Collapse in={!!storageKey && !docRegExp.test(storageKey)}>
          <Box mt={2}>
            <Alert severity='warning'>
              The file you selected (<strong>{extension}</strong>) may not work
              properly. Please select a proper document file (e.g.{' '}
              <strong>.doc</strong>, <strong>.docx</strong>).
            </Alert>
          </Box>
        </Collapse>
        <Collapse in={!!error}>
          <Box mt={2}>
            <Alert severity='error'>
              {error?.message || 'An error occurred.'}
            </Alert>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Fade in={canUndo}>
          <Button
            variant='outlined'
            disabled={!canUndo || isLoading}
            onClick={() => setFile(initialFile)}
          >
            Undo
          </Button>
        </Fade>
        <div style={{ flex: 1 }} />
        {cancelLabel && (
          <Button onClick={() => setOpen(false)}>{cancelLabel}</Button>
        )}
        {!hideUnsetButton && initialFile && (
          <Button
            color='primary'
            variant='outlined'
            disabled={isLoading}
            onClick={() => onSelectFile?.(undefined)}
          >
            {unsetLabel}
          </Button>
        )}
        <Button
          color='primary'
          variant='contained'
          disabled={
            !forceConfirmButton && (disableConfirmButton || !file || isLoading)
          }
          onClick={() => onSelectFile?.(file)}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentTemplateDialog;
